/**
 * Default error messages for different languages
 * These match the translation key 'anErrorOccurred' in locales files
 */
export const DEFAULT_ERROR_MESSAGES = {
  ge: 'მოხდა შეცდომა, გთხოვთ სცადოთ მოგვიანებით...',
  en: 'An error occurred, please try again later...',
} as const

/**
 * Default error message (Georgian) - kept for backward compatibility
 * Use getDefaultErrorMessage() or pass t('anErrorOccurred') in React components for i18n support
 */
export const DEFAULT_ERROR_MESSAGE = DEFAULT_ERROR_MESSAGES.ge

/**
 * Gets the default error message for a given language
 * @param lang - Language code ('ge' | 'en'), defaults to 'ge'
 * @returns Default error message for the specified language
 */
export function getDefaultErrorMessage(lang: 'ge' | 'en' = 'ge'): string {
  return DEFAULT_ERROR_MESSAGES[lang]
}

/**
 * Gets the default error message based on current i18n language
 * Uses i18next instance to detect current language
 * @returns Default error message for the current language
 */
export function getDefaultErrorMessageByLanguage(): string {
  try {
    // Dynamic import to avoid circular dependencies and ensure i18n is initialized
    const i18n = require('../i18n').default
    const currentLang = i18n?.language || 'ge'
    return DEFAULT_ERROR_MESSAGES[currentLang as 'ge' | 'en'] || DEFAULT_ERROR_MESSAGE
  } catch {
    // Fallback if i18n is not available
    return DEFAULT_ERROR_MESSAGE
  }
}

/**
 * Extracts error message from various error formats
 * Supports multiple error response structures:
 * - errors array with nested message object: {errors: [{message: {code: "...", property: "..."}}]}
 * - errors array with string message: {errors: [{message: "..."}]}
 * - direct message object: {message: {code: "...", property: "..."}}
 * - direct string message: {message: "..."}
 * - top-level error message: {message: "..."}
 *
 * @param error - The error object to extract message from
 * @param defaultMessage - Optional custom default message.
 *                         In React components, use t('anErrorOccurred') for i18n support.
 *                         Can also use getDefaultErrorMessage(lang) or getDefaultErrorMessageByLanguage().
 *                         Defaults to Georgian message if not provided.
 * @param customErrorMap - Optional map of error messages to custom translations.
 *                         Use this to override specific error messages with translations.
 *                         Example: { 'Contract session expired': t('serviceTemporarilyUnavailable') }
 * @returns Extracted error message or default message if extraction fails
 *
 * @example
 * // In React components with i18n (recommended):
 * const { t } = useTranslation()
 * const errorMessage = extractErrorMessage(error, t('anErrorOccurred'), {
 *   'Contract session expired': t('serviceTemporarilyUnavailable')
 * })
 *
 * // With language-specific default:
 * const errorMessage = extractErrorMessage(error, getDefaultErrorMessage('en'))
 *
 * // Auto-detect language from i18n:
 * const errorMessage = extractErrorMessage(error, getDefaultErrorMessageByLanguage())
 */
export function extractErrorMessage(
  error: any,
  defaultMessage: string = DEFAULT_ERROR_MESSAGE,
  customErrorMap?: Record<string, string>,
): string {
  if (!error) return defaultMessage

  let extractedMessage: string | null = null

  // Check for errors array with nested message object
  if (error?.data?.errors?.constructor === Array && error.data.errors.length > 0) {
    const firstError = error.data.errors[0]

    // Handle case where message is an object with code/property
    if (firstError?.message && typeof firstError.message === 'object') {
      extractedMessage = firstError.message.code || firstError.message.property || null
    }

    // Handle case where message is a string
    if (typeof firstError?.message === 'string') {
      extractedMessage = firstError.message
    }
  }

  // Fallback to data.message
  if (!extractedMessage && error?.data?.message) {
    if (typeof error.data.message === 'object') {
      extractedMessage = error.data.message.code || error.data.message.property || null
    }
    if (typeof error.data.message === 'string') {
      extractedMessage = error.data.message
    }
  }

  // Fallback to top-level error.message
  if (!extractedMessage && error?.message && typeof error.message === 'string') {
    extractedMessage = error.message
  }

  // Check if extracted message matches any custom error map
  if (extractedMessage && customErrorMap && customErrorMap[extractedMessage]) {
    return customErrorMap[extractedMessage]
  }

  return extractedMessage || defaultMessage
}
